import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import { EmployeeFormDialog } from "@/components/employees/EmployeeFormDialog";
import { EmployeeRow } from "@/components/employees/EmployeeRow";
import { EmployeeCard } from "@/components/employees/EmployeeCard";

const BLANK = {
  name: "",
  role: "welder",
  active: true,
  username: "",
  email: "",
  phone: "",
  pin: "",
  high_contrast: false,
  dark_mode: false,
};

export default function Employees() {
  const { canWrite, canDelete, isAdmin } = useRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setError("");
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "",
      role: e.role || "welder",
      active: e.active ?? true,
      username: e.username || "",
      email: e.email || "",
      phone: e.phone || "",
      pin: "", // never pre-fill — PIN is write-only
      high_contrast: !!e.high_contrast,
      dark_mode: !!e.dark_mode,
    });
    setError("");
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Client-side PIN sanity check
    if (form.pin && (!/^\d{6}$/.test(form.pin))) {
      setError("PIN must be exactly 6 digits.");
      return;
    }
    if (form.username && !form.pin && !editing) {
      setError("New employees with a username must also have a PIN set.");
      return;
    }
    try {
      const payload = { ...form };
      // Don't send empty pin on edit (preserves existing pin_hash)
      if (!payload.pin) delete payload.pin;
      // Trim empty optional strings to null
      ["username", "email", "phone"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee created");
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const onDelete = async (e) => {
    if (!window.confirm(`Delete ${e.name}?`)) return;
    await api.delete(`/employees/${e.id}`);
    toast.success("Employee deleted");
    load();
  };

  const onClearPin = async (e) => {
    if (!window.confirm(`Revoke ${e.name}'s login? They will no longer be able to sign in.`)) return;
    await api.delete(`/employees/${e.id}/pin`);
    toast.success("Login revoked");
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#39678f] subtitle mb-2">
            Workshop
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
            Employees
          </h1>
          <p className="text-sm text-[#50493b] mt-1">
            Welders, testers and sign-off personnel. Set a username + 6-digit PIN
            to give an employee shop-floor login access.
          </p>
        </div>
        <EmployeeFormDialog
          open={open}
          onOpenChange={setOpen}
          onNew={openNew}
          editing={editing}
          form={form}
          setForm={setForm}
          error={error}
          onSubmit={onSubmit}
          canWrite={canWrite}
          isAdmin={isAdmin}
        />
      </div>

      <div className="hidden xl:block bg-white border border-[#33424f]/20 overflow-x-auto">
        <table className="w-full data-table text-sm min-w-[640px]">
          <thead>
            <tr>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Role</th>
              <th className="text-left px-6 py-3">Login</th>
              <th className="text-left px-6 py-3">Active</th>
              <th className="text-right px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="employees-table-body">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-[#50493b]">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-[#50493b]">No employees yet.</td>
              </tr>
            ) : (
              rows.map((e) => (
                <EmployeeRow
                  key={e.id}
                  employee={e}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  isAdmin={isAdmin}
                  openEdit={openEdit}
                  onDelete={onDelete}
                  onClearPin={onClearPin}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="xl:hidden bg-white border border-[#33424f]/20 divide-y divide-[#33424f]/10" data-testid="employees-card-list">
        {loading ? (
          <div className="px-4 py-10 text-center text-[#50493b] text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-[#50493b] text-sm">No employees yet.</div>
        ) : (
          rows.map((e) => (
            <EmployeeCard
              key={e.id}
              employee={e}
              canWrite={canWrite}
              canDelete={canDelete}
              isAdmin={isAdmin}
              openEdit={openEdit}
              onDelete={onDelete}
              onClearPin={onClearPin}
            />
          ))
        )}
      </div>
    </div>
  );
}
